import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import GlassCard from "components/GlassCard";
import StandardLoadingIndicator from "components/StandardLoadingIndicator";
import StandardMessageDisplay from "components/StandardMessageDisplay";
import { getCategoryColor } from "pages/digital-presence/data/social-media/categoryColorMapping";

/**
 * MarketCategoryEngagement
 *
 * Pure renderer — receives pre-computed categoryData from the page.
 *
 * categoryData: [{ category, mean, count }] sorted descending by mean.
 * mean = market-normalised vsMarket (post raw engagement / market baseline).
 * 1.0 = exactly at market average. >1.0 = above market average.
 *
 * This is intentionally different from the per-company vsAvg shown on feed
 * cards. See content-strategy/index.js computeCategoryData() for the formula.
 */
function MarketCategoryEngagement({ categoryData, loading, error }) {
  const maxMean = (categoryData || []).reduce((m, d) => Math.max(m, d.mean), 0);

  return (
    <GlassCard>
      <VuiBox mb={1.5}>
        <VuiTypography variant="h6" color="white" fontWeight="bold" sx={{ fontSize: "1rem" }}>
          Market Engagement by Category
        </VuiTypography>
        <VuiTypography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}>
          Engagement vs market average — last quarter · 1.0× = market baseline
        </VuiTypography>
      </VuiBox>

      {loading ? (
        <StandardLoadingIndicator containerHeight="280px" />
      ) : error ? (
        <StandardMessageDisplay type="error" containerHeight="280px" />
      ) : !categoryData || categoryData.length === 0 ? (
        <StandardMessageDisplay
          type="noData"
          title="No engagement data"
          message="No posts with engagement data found for the selected period."
          containerHeight="280px"
        />
      ) : (
        <VuiBox sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
          {categoryData.map(({ category, mean, count }) => {
            const barPct = maxMean > 0 ? (mean / maxMean) * 100 : 0;
            const color = getCategoryColor(category);
            const isAboveAvg = mean >= 1.0;
            return (
              <VuiBox key={category}>
                <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <VuiTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.85)", fontSize: "0.78rem", fontWeight: 500 }}
                  >
                    {category}
                  </VuiTypography>
                  <VuiBox display="flex" alignItems="center" gap={1}>
                    <VuiTypography
                      variant="caption"
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: isAboveAvg ? "#4ade80" : "rgba(255,255,255,0.5)",
                      }}
                    >
                      {mean.toFixed(2)}×
                    </VuiTypography>
                    <VuiTypography
                      variant="caption"
                      sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.35)" }}
                    >
                      {count} post{count !== 1 ? "s" : ""}
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
                <VuiBox
                  sx={{
                    height: "6px",
                    borderRadius: "3px",
                    background: "rgba(255,255,255,0.08)",
                    overflow: "hidden",
                  }}
                >
                  <VuiBox
                    sx={{
                      height: "100%",
                      width: `${barPct}%`,
                      borderRadius: "3px",
                      background: color,
                      transition: "width 0.6s ease",
                    }}
                  />
                </VuiBox>
              </VuiBox>
            );
          })}
          <VuiTypography
            variant="caption"
            sx={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.3)", mt: 0.5 }}
          >
            green = above market average · baseline = mean engagement across all competitor posts
          </VuiTypography>
        </VuiBox>
      )}
    </GlassCard>
  );
}

MarketCategoryEngagement.propTypes = {
  categoryData: PropTypes.array,
  loading: PropTypes.bool,
  error: PropTypes.bool,
};

export default MarketCategoryEngagement;
